import { type PartialPropsOf } from '@blaze-runtime/engine-domain';

import {
  type ElementLabelWidthProps,
  getClearElementContentOverridesPropsPatchWithSimpleAlign,
  getLabelWidthOverride,
  shouldPatchProp,
} from '../../utils/elements/props-builder.utils';
import { PropsBuilder } from '../__base/props-builder';
import {
  type FormChildrenContentOverrides,
  type FormChildrenStyleOverrides,
} from '../form-container/form-container.model';

export class AudioRecorderPropsBuilder extends PropsBuilder<'audioRecorder'> {
  override setParentContentProps = (
    elementProps: PartialPropsOf<'audioRecorder'>,
    overrides: FormChildrenContentOverrides | null,
    initialValues?: PartialPropsOf<'audioRecorder'>,
  ): this => {
    if (overrides === null) {
      this.patchProps(getClearElementContentOverridesPropsPatchWithSimpleAlign());
      return this;
    }

    if (
      shouldPatchProp(
        overrides.label?.position,
        'content.title.position',
        elementProps,
        initialValues,
      )
    ) {
      this.patchProps({ content: { title: { position: overrides.label.position } } });
    }

    if (
      shouldPatchProp(overrides.label?.align, 'content.title.align', elementProps, initialValues)
    ) {
      this.patchProps({ content: { title: { align: overrides.label.align } } });
    }

    const titlePatch = getLabelWidthOverride(overrides, elementProps as ElementLabelWidthProps);
    if (titlePatch) {
      this.patchProps(titlePatch);
    }

    return this;
  };
  override setParentStyleProps = (
    elementProps: PartialPropsOf<'audioRecorder'>,
    overrides: FormChildrenStyleOverrides | null,
    initialValues?: PartialPropsOf<'audioRecorder'>,
  ): this => {
    if (overrides === null) {
      this.patchProps({
        style: {
          general: { title: undefined },
          label: {
            font: { fontFamily: undefined, fontSize: undefined, style: undefined },
          },
        },
      });
      return this;
    }

    const { titleLabel, fieldLabel, fieldBox } = overrides;

    if (shouldPatchProp(titleLabel?.color, 'style.general.title', elementProps, initialValues)) {
      this.patchProps({ style: { general: { title: titleLabel.color } } });
    }

    if (
      shouldPatchProp(
        fieldLabel?.fontFamily,
        'style.label.font.fontFamily',
        elementProps,
        initialValues,
      )
    ) {
      this.patchProps({ style: { label: { font: { fontFamily: fieldLabel.fontFamily } } } });
    }
    if (
      shouldPatchProp(
        fieldLabel?.fontSize,
        'style.label.font.fontSize',
        elementProps,
        initialValues,
      )
    ) {
      this.patchProps({ style: { label: { font: { fontSize: fieldLabel.fontSize } } } });
    }
    if (shouldPatchProp(fieldLabel?.style, 'style.label.font.style', elementProps, initialValues)) {
      this.patchProps({ style: { label: { font: { style: fieldLabel.style } } } });
    }
    if (shouldPatchProp(fieldLabel?.color, 'style.label.idle.color', elementProps, initialValues)) {
      this.patchProps({ style: { label: { idle: { color: fieldLabel.color } } } });
      this.patchProps({ style: { label: { recording: { color: fieldLabel.color } } } });
    }

    if (shouldPatchProp(fieldBox?.accent, 'style.button.idle.fill', elementProps, initialValues)) {
      this.patchProps({ style: { button: { idle: { fill: fieldBox.accent } } } });
      this.patchProps({ style: { button: { recording: { fill: fieldBox.accent } } } });
    }

    return this;
  };

  constructor(props: PartialPropsOf<'audioRecorder'> | undefined) {
    super(props, { hasData: true, hasLabel: true });
  }
}
